import { supabase } from './supabaseClient';

const BONUS_THRESHOLD = 1.2; // 20% overachievement triggers bonus points

export const kpiService = {
  // A user may be assigned to more than one KPI system, so this returns ALL
  // active assignments (mirrors performanceService.getAssignedPISystems). Using
  // .maybeSingle() here would throw once a user holds >1 system.
  async getAssignedSystems(userId) {
    const { data, error } = await supabase
      .from('user_kpi_system_assignments')
      .select('*, kpi_systems(*)')
      .eq('user_id', userId)
      .eq('is_active', true);
    if (error) throw error;
    return data;
  },

  async getKpiDefinitions(systemId) {
    const { data, error } = await supabase
      .from('kpi_definitions')
      .select('*')
      .eq('kpi_system_id', systemId);
    if (error) throw error;
    return data;
  },

  async getScoringRules(systemId) {
    // Step 1: get definition IDs for this system
    const { data: defs, error: defsError } = await supabase
      .from('kpi_definitions')
      .select('id')
      .eq('kpi_system_id', systemId);
    if (defsError) throw defsError;

    if (!defs || defs.length === 0) return [];

    const defIds = defs.map(d => d.id);

    // Step 2: get scoring rules for those definitions
    const { data, error } = await supabase
      .from('kpi_scoring_rules')
      .select('*')
      .in('kpi_definition_id', defIds);
    if (error) throw error;
    return data;
  },

  async getScoringRulesForDefinition(definitionId) {
    const { data, error } = await supabase
      .from('kpi_scoring_rules')
      .select('*')
      .eq('kpi_definition_id', definitionId);
    if (error) throw error;
    return data;
  },

  async getUserKpis(userId) {
    const { data, error } = await supabase
      .from('kpis')
      .select('*')
      .eq('user_id', userId);
    if (error) throw error;
    return data;
  },

  async getUserKpisInBatch(userIds) {
    if (!userIds || userIds.length === 0) return [];
    const { data, error } = await supabase
      .from('kpis')
      .select('*')
      .in('user_id', userIds);
    if (error) throw error;
    return data;
  },

  async getSubmissionKpis(submissionId) {
    const { data, error } = await supabase
      .from('kpis')
      .select('*')
      .eq('submission_id', submissionId);
    if (error) throw error;
    return data;
  },

  // Upsert one kpis row per item, keyed on (submission_id, kpi_definition_id).
  // status health rule: 'On Track' if value >= target, else 'At Risk'.
  async saveSubmissionKpis(submissionId, userId, period, items) {
    const rows = items.map((it) => ({
      submission_id: submissionId,
      user_id: userId,
      period,
      kpi_definition_id: it.kpi_definition_id,
      current_value: it.current_value,
      target_value: it.target_value,
      kpi_type: it.kpi_type,
      metric_name: it.metric_name,
      status: Number(it.current_value) >= Number(it.target_value) ? 'On Track' : 'At Risk',
    }));
    const { data, error } = await supabase
      .from('kpis')
      .upsert(rows, { onConflict: 'submission_id,kpi_definition_id' })
      .select();
    if (error) throw error;
    return data;
  },

  calculateScore(definitions, scoringRules, kpiRecords, period) {
    let totalScore = 0;
    let onTrackCount = 0;
    let atRiskCount = 0;

    const scores = definitions.map((def) => {
      const record = kpiRecords.find(
        (r) => r.kpi_definition_id === def.id && r.period === period
      );
      const rule = scoringRules.find(
        (r) => r.kpi_definition_id === def.id && r.period === period
      );

      if (!record || !rule) {
        atRiskCount++; // no data = not on track
        return { definitionId: def.id, earned: 0, bonus: 0, total: 0 };
      }

      const current = Number(record.current_value) || 0;
      const target = Number(rule.target_value) || 0;
      const pointsForAchievement = Number(rule.points_for_achievement) || 0;
      const bonusPoints = Number(rule.bonus_points) || 0;

      const earned = current >= target ? pointsForAchievement : 0;
      const bonus = current >= target * BONUS_THRESHOLD ? bonusPoints : 0;
      const total = earned + bonus;

      totalScore += total;

      if (earned > 0) {
        onTrackCount += 1;
      } else {
        atRiskCount += 1;
      }

      return { definitionId: def.id, earned, bonus, total };
    });

    return { scores, totalScore, onTrackCount, atRiskCount };
  },
};
